// SessionTimeoutManager.js
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { logout } from "../slices/authSlice";
import { apiSlice } from "../slices/apiSlice";
import { calendarApiSlice } from "../slices/calendarApiSlice";
import { leaveApiSlice } from "../slices/leaveApiSlice";
import { overtimeApiSlice } from "../slices/overtimeApiSlice";
import { attendanceApiSlice } from "../slices/attendanceApiSlice";

const SessionTimeoutManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isActive, setIsActive] = useState(true);

  // Define routes where session timeout should not be active
  const excludedRoutes = ["/"]; // Add your landing page route(s) here

  const SESSION_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes total session timeout* 60 *
  const WARNING_TIME_MS = 2 * 60 * 1000; // 2 minutes warning before expiry

  useEffect(() => {
    // Check if current route should be excluded from session timeout
    const isExcludedRoute = excludedRoutes.includes(location.pathname);

    if (isExcludedRoute) {
      return; // Exit early if on excluded route
    }
    let inactivityTimer;
    let warningTimer;
    let warningDisplayed = false;

    // Reset the timer when user activity is detected
    const resetTimer = () => {
      // If there's already a warning displayed, don't reset timers
      if (warningDisplayed) return;

      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);

      // Set warning timer to show warning before timeout
      warningTimer = setTimeout(() => {
        showTimeoutWarning();
      }, SESSION_TIMEOUT_MS - WARNING_TIME_MS);

      // Set main timer for session expiration
      inactivityTimer = setTimeout(() => {
        handleSessionTimeout();
      }, SESSION_TIMEOUT_MS);
    };

    const showTimeoutWarning = () => {
      warningDisplayed = true;

      toast("Session Timeout Warning", {
        description: "Your session is about to expire due to inactivity.",
        duration: WARNING_TIME_MS,
        action: {
          label: "Stay Logged In",
          onClick: () => {
            // User clicked "Stay Logged In"
            extendSession();
          },
        },
        cancel: {
          label: "Logout",
          onClick: () => {
            // User clicked "Logout"
            handleSessionTimeout();
          },
        },
        onDismiss: (t) => {
          warningDisplayed = false;
        },
        onAutoClose: (t) => {
          warningDisplayed = false;
          // If closed by timer, the main inactivityTimer will handle logout,
          // but we can also force it here to be sure.
          // Actually inactivityTimer is robust enough.
          // But wait, sonner's duration doesn't trigger a specific callback other than onDismiss/onAutoClose.
          // The inactivityTimer is synchronized with WARNING_TIME_MS so it should work fine automatically.
        },
      });
    };

    const extendSession = () => {
      // Reset timers after successful keep-alive
      warningDisplayed = false;
      toast.dismiss(); // Dismiss the warning toast if it is still open
      resetTimer();

      // Optional: Make API call to keep session alive on backend
      /* 
      fetch('/api/auth/keep-alive', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      */
    };

    const handleSessionTimeout = () => {
      // Clear all RTK Query cache
      dispatch(apiSlice.util.resetApiState());
      dispatch(calendarApiSlice.util.resetApiState());
      dispatch(leaveApiSlice.util.resetApiState());
      dispatch(overtimeApiSlice.util.resetApiState());
      dispatch(attendanceApiSlice.util.resetApiState());

      // Clear auth state and all storage
      dispatch(logout());

      // Clear cookies
      document.cookie =
        "JWT-TOKEN=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

      // Show session expired message
      toast.error("Session Expired", {
        description:
          "Your session has expired due to inactivity. You will be redirected to the login page.",
      });
      // Redirect to login page
      navigate("/login");
    };

    // Events to track user activity - be more selective to avoid constant resets
    const events = ["mousedown", "keypress", "touchstart"];

    // Add event listeners
    const handleUserActivity = () => {
      setIsActive(true);
      resetTimer();
    };

    events.forEach((event) => {
      document.addEventListener(event, handleUserActivity);
    });

    // Initialize timers
    resetTimer();

    // Cleanup
    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
      clearTimeout(inactivityTimer);
      clearTimeout(warningTimer);
    };
  }, [navigate, location.pathname]); // Added location.pathname to dependencies

  return null;
};

export default SessionTimeoutManager;
